import { createSseParser, type SseMessage } from './parser';

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface BackoffOptions { baseMs?: number; maxMs?: number; jitter?: number }

/** Backoff luỹ thừa có jitter: base·2^attempt, tối đa maxMs, ±jitter. */
export function backoffDelay(attempt: number, o: BackoffOptions = {}, random: () => number = Math.random): number {
  const { baseMs = 1000, maxMs = 30_000, jitter = 0.2 } = o;
  const raw = Math.min(maxMs, baseMs * 2 ** attempt);
  const spread = raw * jitter;
  return Math.max(0, Math.round(raw - spread + random() * 2 * spread));
}

export interface EventStreamOptions {
  url: string;
  getToken: () => string | null;
  /** Gọi khi server trả 401: thử làm mới phiên. true ⇒ kết nối lại ngay. */
  onUnauthorized?: () => Promise<boolean>;
  onMessage: (m: SseMessage) => void;
  onStatus?: (s: StreamStatus) => void;
  /** Gọi sau mỗi lần kết nối LẠI thành công – nơi đồng bộ lại dữ liệu có thể đã lỡ. */
  onReconnected?: () => void;
  fetchImpl?: typeof fetch;
  backoff?: BackoffOptions;
  /** Thay công thức backoff mặc định (v3: 1 s, 2 s, 5 s, tối đa 10 s). */
  delayFor?: (attempt: number) => number;
  /** Không nhận byte nào trong khoảng này ⇒ ngắt và kết nối lại (v3: 45 s). Bỏ trống = không theo dõi. */
  idleTimeoutMs?: number;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  random?: () => number;
}

const abortableSleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
  });

export function createEventStream(o: EventStreamOptions) {
  const fetchImpl = o.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const sleep = o.sleep ?? abortableSleep;
  let ctrl: AbortController | null = null;
  let lastEventId: string | undefined;

  async function run(signal: AbortSignal) {
    let attempt = 0;
    let everOpened = false;
    o.onStatus?.('connecting');
    while (!signal.aborted) {
      // Mỗi lần kết nối có controller riêng để bộ đếm "im lặng quá lâu" ngắt được mà không dừng cả vòng lặp.
      const conn = new AbortController();
      const onOuterAbort = () => conn.abort();
      signal.addEventListener('abort', onOuterAbort, { once: true });
      let idle: ReturnType<typeof setTimeout> | undefined;
      const armIdle = () => {
        if (!o.idleTimeoutMs) return;
        if (idle) clearTimeout(idle);
        idle = setTimeout(() => conn.abort(), o.idleTimeoutMs);
      };
      try {
        const headers: Record<string, string> = { Accept: 'text/event-stream', 'Cache-Control': 'no-cache' };
        const token = o.getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        if (lastEventId) headers['Last-Event-ID'] = lastEventId;

        armIdle();
        const res = await fetchImpl(o.url, { headers, signal: conn.signal, credentials: 'include' });
        if (res.status === 401 && o.onUnauthorized && (await o.onUnauthorized())) continue;
        if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);

        o.onStatus?.('open');
        if (everOpened) o.onReconnected?.();
        everOpened = true;
        attempt = 0;

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        const parser = createSseParser((m) => { if (m.id) lastEventId = m.id; o.onMessage(m); });
        armIdle();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          armIdle();
          parser.push(dec.decode(value, { stream: true }));
        }
      } catch {
        if (signal.aborted) break;
      } finally {
        if (idle) clearTimeout(idle);
        signal.removeEventListener('abort', onOuterAbort);
      }
      if (signal.aborted) break;
      o.onStatus?.('reconnecting');
      const delay = o.delayFor ? o.delayFor(attempt++) : backoffDelay(attempt++, o.backoff, o.random);
      await sleep(delay, signal);
    }
    o.onStatus?.('closed');
  }

  return {
    start() {
      if (ctrl) return;
      ctrl = new AbortController();
      void run(ctrl.signal);
    },
    stop() {
      ctrl?.abort();
      ctrl = null;
    },
  };
}
